import { useSnapshotDrill } from "../../hooks/networth";

interface Props {
  stichtag: string;
  onClose: () => void;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function formatStichtag(iso: string): string {
  const parts = iso.split("-");
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

export function SnapshotDrillModal({ stichtag, onClose }: Props) {
  const detail = useSnapshotDrill(stichtag);
  const data = detail.data;

  const totalCur = data
    ? data.classes.LIQUIDE +
      data.classes.ANLAGEN +
      data.classes.IMMOBILIE +
      data.classes.SONSTIGE -
      data.classes.VERBINDLICHKEITEN
    : 0;
  const totalPrev =
    data?.prevClasses != null
      ? data.prevClasses.LIQUIDE +
        data.prevClasses.ANLAGEN +
        data.prevClasses.IMMOBILIE +
        data.prevClasses.SONSTIGE -
        data.prevClasses.VERBINDLICHKEITEN
      : null;
  const totalDelta = totalPrev !== null ? totalCur - totalPrev : null;
  const totalPct =
    totalPrev !== null && totalPrev !== 0 && totalDelta !== null
      ? (totalDelta / totalPrev) * 100
      : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-5 py-4 border-b sticky top-0 flex justify-between items-center"
          style={{ borderColor: "var(--border)", background: "var(--card)" }}
        >
          <div>
            <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>
              Snapshot
            </div>
            <h2 className="text-base font-semibold">{formatStichtag(stichtag)}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-2"
            style={{ color: "var(--muted)" }}
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-5">
          {detail.isLoading && (
            <p className="text-sm text-center py-8" style={{ color: "var(--muted)" }}>
              Wird geladen …
            </p>
          )}

          {data && (
            <>
              <div>
                <div className="text-xs" style={{ color: "var(--muted)" }}>
                  Nettovermögen
                </div>
                <div className="text-2xl font-bold num">{formatEur(totalCur)}</div>
                {totalDelta !== null && (
                  <div
                    className="text-xs num mt-0.5"
                    style={{ color: totalDelta >= 0 ? "var(--pos)" : "var(--neg)" }}
                  >
                    {totalDelta >= 0 ? "↗" : "↘"} {totalDelta >= 0 ? "+" : ""}
                    {formatEur(totalDelta)}
                    {totalPct !== null && ` (${totalPct >= 0 ? "+" : ""}${totalPct.toFixed(1)}%)`}
                    {data.prevStichtag && ` seit ${formatStichtag(data.prevStichtag)}`}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Klassen</h3>
                <table className="w-full text-sm">
                  <tbody>
                    {(
                      ["LIQUIDE", "ANLAGEN", "IMMOBILIE", "SONSTIGE", "VERBINDLICHKEITEN"] as const
                    ).map((k) => {
                      const cur = data.classes[k];
                      const prev = data.prevClasses?.[k];
                      const delta = prev !== undefined ? cur - prev : null;
                      return (
                        <tr
                          key={k}
                          className="border-b"
                          style={{ borderColor: "var(--border-soft)" }}
                        >
                          <td className="py-1.5 capitalize">{k.toLowerCase()}</td>
                          <td className="py-1.5 text-right num">{formatEur(cur)}</td>
                          <td
                            className="py-1.5 text-right num text-xs"
                            style={{
                              color:
                                delta === null
                                  ? "var(--muted)"
                                  : delta > 0
                                    ? "var(--pos)"
                                    : delta < 0
                                      ? "var(--neg)"
                                      : "var(--muted)",
                            }}
                          >
                            {delta === null
                              ? "—"
                              : delta === 0
                                ? "→ 0 €"
                                : delta > 0
                                  ? `↗ +${formatEur(delta)}`
                                  : `↘ ${formatEur(delta)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {data.topMovers.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Top-Mover seit {data.prevStichtag && formatStichtag(data.prevStichtag)}
                    {data.daysSincePrev !== null && data.daysSincePrev > 30 && (
                      <span className="ml-2 text-xs font-normal" style={{ color: "var(--muted)" }}>
                        (vor {data.daysSincePrev} Tagen)
                      </span>
                    )}
                  </h3>
                  <table className="w-full text-sm">
                    <tbody>
                      {data.topMovers.map((m) => (
                        <tr
                          key={m.investmentId}
                          className="border-b"
                          style={{ borderColor: "var(--border-soft)" }}
                        >
                          <td className="py-2">
                            <div className="font-medium">{m.name}</div>
                            <div className="text-xs" style={{ color: "var(--muted)" }}>
                              {m.symbol}
                            </div>
                          </td>
                          <td
                            className="py-2 text-right num"
                            style={{
                              color: m.deltaEur >= 0 ? "var(--pos)" : "var(--neg)",
                            }}
                          >
                            <div className="font-semibold">
                              {m.deltaEur >= 0 ? "+" : ""}
                              {formatEur(m.deltaEur)}
                            </div>
                            <div className="text-xs">
                              {m.deltaPercent !== null
                                ? `${m.deltaPercent >= 0 ? "+" : ""}${m.deltaPercent.toFixed(1)}%`
                                : ""}
                              {m.note && ` · ${m.note}`}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!data.prevStichtag && (
                <p className="text-xs" style={{ color: "var(--muted)" }}>
                  Erster Snapshot — kein Vorgaenger fuer Vergleich vorhanden.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
